// (c) chaehongee. Confidential and proprietary.

import { useMemo } from 'react'
import type { Size, Rect, SessionLike, PreviewState } from './types'

export interface UsePromptedPreviewArgs {
    previewUrl: string | null
    hoveredNodeId: string | null
    session: SessionLike | null
    containerSize: Size | null
    imageSizesBySrc: Record<string, { w: number; h: number }>
}

export function usePromptedPreview(args: UsePromptedPreviewArgs): PreviewState {
    const { previewUrl, hoveredNodeId, session, containerSize, imageSizesBySrc } = args

    // Determine which image to preview
    const previewSrc = useMemo(() => {
        if (previewUrl) return previewUrl
        const hn = (session?.nodes ?? []).find((n) => n.id === hoveredNodeId)
        return hn?.image_path ?? null
    }, [previewUrl, hoveredNodeId, session?.nodes])

    const previewMediaType = useMemo(() => {
        const hoveredNode = (session?.nodes ?? []).find((n) => n.id === hoveredNodeId)
        if (hoveredNode?.type) return hoveredNode.type
        if (!previewUrl) return 'image'
        const lower = previewUrl.toLowerCase()
        if (/\.(mp4|webm|mov|m4v|ogg)(\?|#|$)/.test(lower)) return 'video'
        return 'image'
    }, [hoveredNodeId, previewUrl, session?.nodes])

    // Determine caption (prompt) from the producing commit metadata
    const captionText = useMemo(() => {
        if (!session?.commits || !hoveredNodeId) return ''
        const c = (session.commits as any[]).find((cc: any) => cc?.node_to_id === hoveredNodeId)
        const meta: Record<string, any> | undefined = c?.commit_metadata ?? undefined
        if (!meta) return ''
        const fromInput = (meta.input && (meta.input.prompt || meta.input.text)) || ''
        const fromLatest = (meta.latest_params && (meta.latest_params.prompt || meta.latest_params.text)) || ''
        return String(fromInput || fromLatest || '')
    }, [session?.commits, hoveredNodeId])

    // Reserve vertical space for caption when present
    const reservedCaptionPx = useMemo(() => {
        if (!captionText || !containerSize) return 0
        const minPx = 64
        const frac = Math.floor(containerSize.h * 0.22)
        return Math.max(minPx, Math.min(frac, 200))
    }, [captionText, containerSize])

    // Compute preview rect, leaving space for caption block
    const previewRect = useMemo<Rect | null>(() => {
        if (!containerSize || !containerSize.w || !containerSize.h) return null
        if (!previewSrc) return null
        const maxW = containerSize.w * 0.9
        const maxH = Math.max(1, containerSize.h * 0.9 - reservedCaptionPx)
        const natural = imageSizesBySrc[previewSrc]
        if (!natural) {
            const w = Math.max(1, maxW)
            const h = Math.max(1, maxH)
            return { x: (containerSize.w - w) / 2, y: (containerSize.h - reservedCaptionPx - h) / 2, w, h }
        }
        const scale = Math.min(maxW / (natural.w || 1), maxH / (natural.h || 1))
        const w = Math.max(1, natural.w * (scale || 1))
        const h = Math.max(1, natural.h * (scale || 1))
        return { x: (containerSize.w - w) / 2, y: (containerSize.h - reservedCaptionPx - h) / 2, w, h }
    }, [containerSize, previewSrc, reservedCaptionPx, imageSizesBySrc])

    // Caption position and scrollable height
    const captionRect = useMemo<Rect | null>(() => {
        if (!captionText || !previewRect) return null
        const h = Math.max(24, reservedCaptionPx ? reservedCaptionPx - 12 : 120)
        return {
            x: previewRect.x,
            y: previewRect.y + previewRect.h + 8,
            w: previewRect.w,
            h,
        }
    }, [captionText, previewRect, reservedCaptionPx])

    return { previewSrc, previewMediaType, previewRect, captionText, captionRect }
}

export default usePromptedPreview

